import React, { useState, useEffect } from "react";
import NumberFlow from "@number-flow/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { Loader2 } from "lucide-react";
import Chart from "./Chart";
import Dailies from "./dailies";
import MarketVolume from "./MarketVolume";

const Volume = ({ competitor }) => {
  const [timeFilter, setTimeFilter] = useState("24h");
  const [userData, setUserData] = useState([]);
  const [caseBattlesData, setCaseBattlesData] = useState({
    wager: 0,
    payout: 0,
    profit: 0,
  });
  const [rouletteData, setRouletteData] = useState({
    wager: 0,
    payout: 0,
    profit: 0,
  });
  const [loadingCaseBattles, setLoadingCaseBattles] = useState(true);
  const [loadingRoulette, setLoadingRoulette] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const fetchUserData = async () => {
      if (!competitor?.name) {
        console.warn("❗ Skipping fetch: No competitor name provided.");
        return;
      }

      setLoadingUsers(true);
      const siteName = competitor.name.toLowerCase();
      const apiUrl = `/.netlify/functions/fetchUserCount?site=${siteName}&range=${timeFilter}`;

      try {
        const token = localStorage.getItem("authToken"); // Assuming token is stored in localStorage
        const response = await fetch(apiUrl, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
          console.error(
            `❌ API request failed: ${response.status} ${response.statusText}`
          );
          return;
        }

        const data = await response.json();

        if (isMounted) {
          const formattedData = data.map((item) => ({
            date: new Date(item.date),
            count: item.count,
          }));
          setUserData(formattedData);
        }
      } catch (error) {
        console.error("⚠️ Error fetching user data:", error);
      } finally {
        if (isMounted) {
          setLoadingUsers(false);
        }
      }
    };

    fetchUserData();
    return () => {
      isMounted = false;
    };
  }, [timeFilter, competitor?.name]);

  useEffect(() => {
    let isMounted = true;

    const fetchCaseBattlesData = async () => {
      if (!competitor?.name) {
        console.warn("❗ Skipping fetch: No competitor name provided.");
        return;
      }

      setLoadingCaseBattles(true);
      const siteName = competitor.name.toLowerCase();
      const apiUrl = `/.netlify/functions/fetchCaseBattles?site=${siteName}&range=${timeFilter}`;

      try {
        const token = localStorage.getItem("authToken");

        if (!token) {
          console.warn("⚠️ No auth token found, skipping fetch.");
          setLoadingCaseBattles(false);
          return;
        }

        const response = await fetch(apiUrl, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
          console.error(
            `❌ API request failed: ${response.status} ${response.statusText}`
          );
          return;
        }

        const data = await response.json();

        if (isMounted) {
          setCaseBattlesData({
            wager: data.wager || 0,
            payout: data.payout || 0,
            profit: data.profit || 0,
          });
        }
      } catch (error) {
        console.error("⚠️ Error fetching case battles data:", error);
      } finally {
        if (isMounted) {
          setLoadingCaseBattles(false);
        }
      }
    };

    fetchCaseBattlesData();
    return () => {
      isMounted = false;
    };
  }, [timeFilter, competitor?.name]);

  useEffect(() => {
    let isMounted = true;

    const fetchRouletteData = async () => {
      if (!competitor?.name) {
        console.warn("❗ Skipping fetch: No competitor name provided.");
        return;
      }

      setLoadingRoulette(true);
      const siteName = competitor.name.toLowerCase();
      const apiUrl = `/.netlify/functions/fetchRoulette?site=${siteName}&range=${timeFilter}`;

      try {
        const token = localStorage.getItem("authToken");

        if (!token) {
          console.warn("⚠️ No auth token found, skipping fetch.");
          setLoadingRoulette(false);
          return;
        }

        const response = await fetch(apiUrl, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
          console.error(
            `❌ API request failed: ${response.status} ${response.statusText}`
          );
          return;
        }

        const data = await response.json();

        if (isMounted) {
          setRouletteData({
            wager: data.wager || 0,
            payout: data.payout || 0,
            profit: data.profit || 0,
          });
        }
      } catch (error) {
        console.error("⚠️ Error fetching roulette data:", error);
      } finally {
        if (isMounted) {
          setLoadingRoulette(false);
        }
      }
    };

    fetchRouletteData();
    return () => {
      isMounted = false;
    };
  }, [timeFilter, competitor?.name]);

  const formatProfit = (value) => {
    const sign = value >= 0 ? "+" : "";
    return {
      formatted: `${sign}${value}`,
      color: value >= 0 ? "text-green-500" : "text-red-500",
    };
  };

  const caseProfit = formatProfit(caseBattlesData.profit);
  const rouletteProfit = formatProfit(rouletteData.profit);

  const LoadingSpinner = () => (
    <div className="flex items-center justify-center w-full h-full">
      <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
    </div>
  );

  const NumberDisplay = ({ label, value, isLarge = false, profitColor }) => (
    <div className="space-y-1">
      <span className="text-sm text-gray-400">{label}</span>
      {isLarge ? (
        <NumberFlow
          value={Math.round(value * 100) / 100}
          duration={1000}
          className={`text-4xl font-bold ${profitColor || "text-white"} block`}
        />
      ) : (
        <NumberFlow
          value={Math.round(value * 100) / 100}
          duration={1000}
          className="text-2xl text-white block"
        />
      )}
    </div>
  );

  return (
    <div className="w-full space-y-6 mb-6">
      <div className="flex items-center">
        <div className="relative inline-block">
          <select
            value={timeFilter}
            onChange={(e) => setTimeFilter(e.target.value)}
            className="appearance-none bg-neutral-800 text-white pl-4 pr-10 py-2 rounded-lg border border-neutral-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent cursor-pointer hover:bg-neutral-700 transition-colors duration-200"
          >
            <option value="24h">Last 24 hours</option>
            <option value="7d">Last 7 days</option>
            <option value="28d">Last 28 days</option>
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
            <FontAwesomeIcon icon={faChevronDown} size="xs" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Case Battles Card */}
        <div className="bg-neutral-800 p-6 rounded-lg shadow flex flex-col items-center justify-center min-h-full">
          <h2 className="text-xl font-semibold text-white mb-4 text-center">
            Case Battles
          </h2>
          {loadingCaseBattles ? (
            <LoadingSpinner />
          ) : (
            <div className="space-y-2 text-center">
              <NumberDisplay label="Wager" value={caseBattlesData.wager} />
              <NumberDisplay label="Payout" value={caseBattlesData.payout} />
              <NumberDisplay
                label="Profit"
                value={caseBattlesData.profit}
                isLarge
                profitColor={caseProfit.color}
              />
            </div>
          )}
        </div>

        {/* Roulette Card */}
        <div className="bg-neutral-800 p-6 rounded-lg shadow flex flex-col items-center justify-center min-h-full">
          <h2 className="text-xl font-semibold text-white mb-4 text-center">
            Roulette
          </h2>
          {loadingRoulette ? (
            <LoadingSpinner />
          ) : (
            <div className="space-y-2 text-center">
              <NumberDisplay label="Wager" value={rouletteData.wager} />
              <NumberDisplay label="Payout" value={rouletteData.payout} />
              <NumberDisplay
                label="Profit"
                value={rouletteData.profit}
                isLarge
                profitColor={rouletteProfit.color}
              />
            </div>
          )}
        </div>

        {/* User Count Chart Card */}
        <div className="bg-neutral-800 rounded-lg shadow flex flex-col items-center justify-center py-6 min-h-full">
          <h2 className="text-xl font-semibold text-white text-center">
            User Count
          </h2>
          <div className="w-full flex-1 flex items-center justify-center">
            {loadingUsers ? <LoadingSpinner /> : <Chart userData={userData} />}
          </div>
        </div>
      </div>

      {competitor.name.toLowerCase() === "csgoroll" && (
        <div className="w-full flex flex-col md:flex-row gap-6 ">
      

          <div className="w-full bg-neutral-800 p-6 rounded-lg shadow-lg flex flex-col items-center">
            <h2 className="text-xl font-bold mb-4">Free Daily Case Battles</h2>
            <Dailies site={competitor.name} period={timeFilter} />
          </div>
        </div>
      )}

{competitor.name.toLowerCase() === "csgoroll" && (
        <div className="w-full flex flex-col md:flex-row ">
          <div className="bg-neutral-800 w-full px-2 py-6 md:p-8 rounded-lg shadow-lg flex flex-col items-center">
            <h2 className="text-xl font-bold mb-4">All Time P2P Volume</h2>
            <MarketVolume site={competitor.name} period={timeFilter}/>
          </div>

        </div>
      )}
    </div>
  );
};

export default Volume;
